/**
 * Task ageing and gold bounties. See docs/bounties.md.
 *
 * Pure and dependency-free so the same code runs on the server (payout at
 * completion), in the browser (card colours and the "worth N gold" chip) and
 * in Node tests. The client's promise and the server's payout can only agree
 * if both come from bountyFor, and card colours and payouts can only agree if
 * both come from ageSteps.
 */
import { THEME_BONUS } from "./house-week.ts";
import type { Cadence, LateRush, Task } from "./types";
import { isLateRushActive } from "./late-rush.ts";
import { auctionPriceNow, isLuxuryTier, luxuryPayout } from "./economy.ts";
import { sameName } from "./notes.ts";

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/**
 * How long a task waits before it climbs one attention level (orange, red,
 * red with "!"). Daily chores are expected within hours, so they climb every
 * two hours; everything else climbs once a day.
 */
export const AGE_PERIOD_MS: { readonly default: number; readonly daily: number } = {
  default: DAY_MS,
  daily: 2 * HOUR_MS,
};

/**
 * Ageing steps at which a task is urgent: the red "!" level, and the point at
 * which everyone is told it is overdue (server/sweep.ts). Three days for most
 * tasks, six hours for daily chores.
 */
export const URGENT_STEPS = 3;

/** Daily chores nobody picks up are announced as missed and removed at this age. */
export const DAILY_CHORE = {
  missedAfterMs: 8 * HOUR_MS,
} as const;

/** How long `task` waits before it counts as urgent. */
export function urgentAfterMs(task: Pick<AgeableTask, "schedule_id" | "cadence">): number {
  return URGENT_STEPS * agePeriodMs(task);
}

/** Tunables. Changing these affects future payouts only; the ledger keeps what was paid. */
export const GOLD = {
  /** Hand-added task. */
  base: 10,
  /** Task spawned by a schedule. Same as base for now, kept separate so it can be tuned. */
  scheduledBase: 10,
  bigJobBase: 100,
  /**
   * Projects (docs/projects.md, flag projectsUI; owner, 2026-11-02, picking "Grows as you work"): a
   * project does not age like a task; each progress photo adds this to its pot instead (at most one a
   * peon a household day, shared/projects.ts progressGold). The pot starts at bigJobBase.
   */
  projectProgress: 20,
  urgentMultiplier: 2,
  /** Index = ageing steps waited (see AGE_PERIOD_MS), capped at the last entry. Matches the card colour ramp. */
  ageMultipliers: [1, 1.5, 2, 2.5] as const,
  /**
   * Stale gold (owner, 2026-10-05: "After a task hits its max staleness multiplier, it should
   * increase bounty by 5 gold flat per day"). Once a task's ageing steps reach the last
   * ageMultipliers entry, every further whole step (a whole day: daily chores never take the
   * waiting ramp, so they never get here) adds `perDay` gold on top. Flat: urgent, big job and the
   * bonus in force never multiply it; the overdue reminder and task roulette, which double the
   * whole payout, double it too. See staleGold.
   */
  stale: { perDay: 5 },
  /**
   * Quick bonus: a weekly or monthly chore finished within a day of being posted (or done
   * early before it was posted) pays ten times its base; a hand-added task finished within an
   * hour of being added pays five times. It replaces the age multiplier. A daily chore done
   * within its day pays three times (see isDailyChore).
   */
  quick: {
    scheduledMultiplier: 10,
    /** Daily chores come round every day, so their bonus is smaller and never applies early. */
    dailyMultiplier: 3,
    scheduledWindowMs: 24 * 60 * 60 * 1000,
    adHocMultiplier: 5,
    adHocWindowMs: 60 * 60 * 1000,
  },
  /**
   * Overdue reminder (owner, 2026-10-01): for fifteen minutes after a task's overdue push went
   * out (tasks.alerted_at, server/sweep.ts), finishing it pays double. It stacks on top of the
   * usual payout, because an overdue task already sits at the 2.5x waiting multiplier and a
   * competing 2x would never win. Luxury tasks never get it.
   */
  reminder: {
    multiplier: 2,
    windowMs: 15 * 60 * 1000,
  },
  /**
   * Bounty streak (docs/bounties.md "Streak", flag streakUI for the word only): a peon who finished
   * at least one non-luxury task on each of two household-local days in a row earns `multiplier`
   * on their bounties from the third such day on, while the streak holds. It competes with the
   * waiting, quick and gold-rush bonuses under the largest-single-multiplier rule, never stacks
   * with them, and never applies to luxury tasks. A day with nothing breaks it.
   */
  streak: {
    multiplier: 1.5,
    /** Days in a row, counting the day of the completion, from which the multiplier applies. */
    fromDay: 3,
  },
  /**
   * Task roulette (docs/roulette.md): the peon the wheel picked earns this on the task. Owner,
   * 2026-10-05: "The assignee should earn 2x gold on the task and this stacks with all other
   * bonuses". It multiplies the finished total, after every other bonus, never competing with them.
   */
  rouletteMultiplier: 2,
  /**
   * A line on the weekly scroll (docs/weekly.md "Gold for a line"). Owner, 2026-10-05: "Posting an
   * appreciation or accomplishment yields 10 gold". Paid to the writer the moment the line is
   * written (a 'scroll' ledger row, server/weekly.ts addEntry) and taken back when the line comes
   * off (deleteEntry). Flat, per line, no cap: see docs/economy.md "Gold for a line" on farming.
   */
  scrollLine: 10,
  /**
   * A heart on a pinned note (docs/notes.md "Hearts", docs/economy.md "Gold for a heart on a note").
   * Owner, 2026-10-12: "When a note is liked, the user placing the note gets 10 gold, max 50 per day
   * so it can't be gamed". Paid to the note's author when someone else hearts it (a 'heart' ledger
   * row, server/db.ts setNoteHeart), once per note and hearter, at most
   * shared/notes.ts NOTE_HEART_GOLD_DAILY_CAP an author a household day. Never taken back.
   */
  noteHeart: 10,
} as const;

export type AgeableTask = Pick<Task, "created_at" | "due_at"> & Partial<Pick<Task, "schedule_id" | "cadence">>;
/**
 * `project` (docs/projects.md): the gold a big job's progress photos have grown its pot by, set only while
 * projectsUI is on (shared/projects.ts asBountyTask). A number pays the job as a project: the big-job base
 * plus that growth, never the waiting ramp, stale gold or the quick bonus (the pot grows by work shown, not
 * by time or speed). The gold rush, the streak, the reminder, the wheel and the theme apply as to any task.
 * Absent or null, a big job pays as before.
 */
export type BountyTask = AgeableTask & Pick<Task, "urgent"> & Partial<Pick<Task, "big_job" | "luxury" | "created_by">> & { alerted_at?: string | null; project?: number | null };

export interface BountyBreakdown {
  /** The task's own base: the big-job base for a big job, else the hand-added or scheduled base. */
  base: number;
  /**
   * True only when the urgent doubling is the one in force. Big job and urgent never stack: the
   * greater of the big-job base and base × urgentMultiplier pays, so an urgent big job reports
   * bigJob and not urgent.
   */
  urgent: boolean;
  /** True when the big-job base is the one in force (see urgent). */
  bigJob: boolean;
  /** Whole ageing periods since the task started waiting, never negative. */
  ageSteps: number;
  /** Length of one ageing period for this task, ms. */
  agePeriodMs: number;
  ageMultiplier: number;
  /**
   * Flat gold for each whole day the task has waited past the day it reached the top waiting
   * multiplier (GOLD.stale, staleGold), before the overdue reminder and roulette doubles. 0 until
   * then. Optional so older ledger rows and hand-built breakdowns still type.
   */
  staleGold?: number;
  /** The quick bonus multiplier in force (10 or 5), or null when the window has passed. */
  quickMultiplier: number | null;
  /**
   * The gold-rush boost passed to bountyFor, when it is the strict winner over
   * both the quick bonus and the age multiplier; null otherwise, including
   * when a boost was passed but did not win (so the badge can say which
   * bonus paid). See shared/flash.ts.
   */
  flashMultiplier: number | null;
  /**
   * The overdue reminder doubling (GOLD.reminder.multiplier) when finished within
   * GOLD.reminder.windowMs of the overdue push, else null. It multiplies on top of the bonus in
   * force rather than competing with it.
   */
  reminderMultiplier: number | null;
  /**
   * The streak multiplier (GOLD.streak.multiplier) when it is the strict winner over the waiting,
   * quick and gold-rush multipliers, else null (including when the streak held but another bonus
   * paid as much or more). Optional so older ledger rows and hand-built breakdowns still type.
   */
  streak?: number | null;
  /**
   * True when task roulette doubled the pay (docs/roulette.md): the peon the wheel picked finished
   * it. Owner, 2026-10-05: "The assignee should earn 2x gold on the task and this stacks with all
   * other bonuses". Optional so older ledger rows still type.
   */
  roulette?: boolean;
  /**
   * The week's theme bonus (docs/house-week.md, shared/house-week.ts THEME_BONUS): flat gold, half the
   * starting gold, for a chore matching the week's theme while themeWeekUI is on. It stacks, like the
   * stale gold, before the reminder and roulette doubles. Present only when paid.
   */
  themeGold?: number;
  /** Integer gold. */
  total: number;
  /** True for a luxury task: urgency and age never change its payout. */
  luxury: boolean;
  /**
   * Paid as a project (docs/projects.md): the gold its progress photos grew the pot by, on top of the
   * big-job base, with no waiting multiplier and no stale gold. Present only for a project.
   */
  project?: number;
}

/**
 * Daily chores never climb the waiting ramp: they come round every day and are missed after
 * eight hours, so age has nothing to reward. Doing one pays the daily quick bonus instead
 * (GOLD.quick.dailyMultiplier), never an early bonus. Urgent still doubles (a big-job base, being
 * greater, pays instead), and a gold rush competes under the usual largest-single-multiplier rule.
 */
export function isDailyChore(task: Pick<AgeableTask, "schedule_id" | "cadence">): boolean {
  return Boolean(task.schedule_id) && task.cadence === "daily";
}

/**
 * The quick bonus that applies if `task` is finished at `at`: weekly and monthly chores within a
 * day of their posting (also when done early, before the posting), daily chores within their
 * day (not early), hand-added tasks within an hour of being added. Null once the window has passed.
 *
 * `by` is who is finishing it. Owner, 2026-10-04: "Anyone who completes a task they put up does
 * not get a fast multiplier." So a hand-added task finished by the peon who added it (its
 * `created_by`, compared like shared/notes.ts sameName: trimmed, spaces collapsed, any case) gets
 * no quick bonus. Chores are posted by their schedule, not put up by a person, so they keep theirs
 * whoever finishes them. With no `by` (a preview that does not know the finisher) the bonus is
 * shown as possible, as before.
 */
export function quickMultiplier(task: BountyTask, at: number, by: string | null = null): number | null {
  const since = Date.parse(waitingSince(task));
  if (!Number.isFinite(since)) return null;
  const elapsed = at - since;
  if (task.schedule_id) {
    if (elapsed > GOLD.quick.scheduledWindowMs) return null;
    if (isDailyChore(task)) return elapsed >= 0 ? GOLD.quick.dailyMultiplier : null;
    return GOLD.quick.scheduledMultiplier;
  }
  if (by && task.created_by && sameName(by, task.created_by)) return null;
  return elapsed >= 0 && elapsed <= GOLD.quick.adHocWindowMs ? GOLD.quick.adHocMultiplier : null;
}

/**
 * The streak multiplier for a completion that makes `daysInARow` household-local days in a row with
 * at least one non-luxury task done (the completion's own day counted), or null below
 * GOLD.streak.fromDay. See server/streaks.ts streakDays for the count.
 */
export function streakMultiplier(daysInARow: number): number | null {
  return Number.isFinite(daysInARow) && daysInARow >= GOLD.streak.fromDay ? GOLD.streak.multiplier : null;
}

/** The word the task screen's meta line shows when the streak is the bonus in force (flag streakUI). */
export const STREAK_WORD = "Streak";

/** The instant a task started waiting: its scheduled time if it has one, else when it was added. */
export function waitingSince(task: Pick<Task, "created_at" | "due_at">): string {
  return task.due_at ?? task.created_at;
}

/** Length of one ageing period for `task`. */
export function agePeriodMs(task: Pick<AgeableTask, "schedule_id" | "cadence">): number {
  const cadence: Cadence | null | undefined = task.schedule_id ? task.cadence : null;
  return cadence === "daily" ? AGE_PERIOD_MS.daily : AGE_PERIOD_MS.default;
}

/** Whole ageing periods `task` has waited as of `at` (ms since epoch), never negative. */
export function ageSteps(task: AgeableTask, at: number): number {
  const since = Date.parse(waitingSince(task));
  const elapsed = Number.isFinite(since) ? at - since : 0;
  return Math.max(0, Math.floor(elapsed / agePeriodMs(task)));
}

/**
 * Whole days `task` has waited past the step its waiting multiplier reached the top of
 * GOLD.ageMultipliers, as of `at`: the same ageSteps count, minus the cap's step, never negative.
 * Daily chores never take the waiting ramp, so they never have any.
 */
export function staleDays(task: AgeableTask, at: number): number {
  if (isDailyChore(task)) return 0;
  return Math.max(0, ageSteps(task, at) - (GOLD.ageMultipliers.length - 1));
}

/**
 * The flat gold a task earns for staying open past the top waiting multiplier (owner, 2026-10-05:
 * "After a task hits its max staleness multiplier, it should increase bounty by 5 gold flat per
 * day"): GOLD.stale.perDay for each staleDays. 0 at the cap's own day, 5 a day later, 35 a week later.
 */
export function staleGold(task: AgeableTask, at: number): number {
  return staleDays(task, at) * GOLD.stale.perDay;
}

/**
 * Value of `task` if completed at `at` (ms since epoch). `boost` is the gold
 * rush multiplier in force for the peon finishing it (shared/flash.ts's
 * FLASH.multiplier, or null if no rush applies to them), when the caller
 * knows it; existing callers pass none and nothing changes for them.
 *
 * `by` is the peon finishing it (the server's done_by, or the viewer on a
 * client showing what they would earn). Owner, 2026-10-04: "Anyone who
 * completes a task they put up does not get a fast multiplier": see
 * quickMultiplier. Omitted, the quick bonus is shown as possible.
 *
 * `streakDays` is how many household-local days in a row the finisher has done a non-luxury task,
 * this completion's day included (server/streaks.ts streakDays). From GOLD.streak.fromDay the
 * streak multiplier joins the largest-single-multiplier rule. Omitted, no streak applies.
 *
 * `roulette` is true when the finisher is the peon task roulette picked for this task (docs/roulette.md).
 * Unlike every other multiplier it does not compete: it doubles the total after everything else
 * (urgent, the bonus in force, the overdue reminder), so an urgent task the wheel picked you for pays
 * twice the urgent pay. Luxury tasks are never spun, so it never applies to them.
 *
 * `reminder` says which window the overdue reminder's doubling follows (reminderInForce): omitted, or
 * with `lateRushUI` false, fifteen minutes from the overdue push as before; with it on, the
 * finisher's own late chore rush window (docs/bounties.md "Late chore rush").
 */
export function bountyFor(
  task: BountyTask,
  at: number,
  boost: number | null = null,
  by: string | null = null,
  streakDays = 0,
  roulette = false,
  reminder?: ReminderRule,
  themed = false,
): BountyBreakdown {
  if (task.luxury && isLuxuryTier(task.luxury)) {
    // Luxury tasks pay a flat half of their tier; urgency, waiting and the gold rush never change it.
    const base = luxuryPayout(task.luxury);
    return { base, urgent: false, bigJob: false, ageSteps: 0, agePeriodMs: agePeriodMs(task), ageMultiplier: 1, staleGold: 0, quickMultiplier: null, flashMultiplier: null, reminderMultiplier: null, streak: null, total: base, luxury: true };
  }
  const steps = ageSteps(task, at);
  // A project (owner, 2026-11-02, "Grows as you work") does not age: its progress photos grow its pot.
  const projectGrowth = task.big_job && typeof task.project === "number" && Number.isFinite(task.project) ? Math.max(0, Math.round(task.project)) : null;
  // Daily chores keep their age steps (card colours and overdue alerts still climb) but never
  // earn the waiting ramp; their quick bonus is what doing them pays.
  const ageMultiplier = isDailyChore(task) || projectGrowth !== null ? 1 : GOLD.ageMultipliers[Math.min(steps, GOLD.ageMultipliers.length - 1)];
  // A project's pot grows by its progress photos, not by speed: no quick bonus either.
  const quick = projectGrowth !== null ? null : quickMultiplier(task, at, by);
  const regularBase = task.schedule_id ? GOLD.scheduledBase : GOLD.base;
  const base = task.big_job ? GOLD.bigJobBase + (projectGrowth ?? 0) : regularBase;
  // Big job and urgent never stack (owner, 2026-09-30): the greater of the big-job base and the
  // urgent doubling of the regular base pays, and the breakdown says which one did. A tie goes to
  // big job.
  const urgentBase = task.urgent ? regularBase * GOLD.urgentMultiplier : 0;
  const bigJobBase = task.big_job ? base : 0;
  const bigJob = bigJobBase > 0 && bigJobBase >= urgentBase;
  const urgent = !bigJob && urgentBase > 0;
  const startingGold = bigJob ? bigJobBase : urgent ? urgentBase : regularBase;
  // Bonuses never stack: the multiplier in force is the largest single one that applies.
  const streakBonus = streakMultiplier(streakDays);
  const candidates = [ageMultiplier, quick ?? 0, boost ?? 0, streakBonus ?? 0];
  const multiplier = Math.max(...candidates);
  // The badge shows which bonus paid, so flashMultiplier is set only when the boost strictly
  // beat the quick bonus, the age multiplier and the streak; a tie (or losing) reports as null
  // (and the server then spends no gold-rush claim). The streak is reported the same way.
  const flashMultiplier = boost !== null && boost > ageMultiplier && boost > (quick ?? 0) && boost > (streakBonus ?? 0) ? boost : null;
  const streak = streakBonus !== null && streakBonus > ageMultiplier && streakBonus > (quick ?? 0) && streakBonus > (boost ?? 0) ? streakBonus : null;
  // The overdue reminder is the one bonus that stacks: it doubles whatever the above pays.
  // With lateRushUI on its window is the finisher's late chore rush instead (reminderInForce).
  // A task without schedule_id is hand-added (as regularBase reads it above), so no late chore rush.
  const reminderMultiplier = reminderInForce({ alerted_at: task.alerted_at, luxury: task.luxury, schedule_id: task.schedule_id ?? null }, at, reminder)
    ? GOLD.reminder.multiplier
    : null;
  // Stale gold (owner, 2026-10-05) goes on flat after the multiplier in force, whichever won; the
  // two stacking doubles below (reminder, roulette) double it with the rest.
  const stale = projectGrowth !== null ? 0 : staleGold(task, at);
  // The week's theme (owner, 2026-10-29: "matching chores pay extra") adds half the starting gold
  // flat, beside the stale gold, so it pays whichever bonus won.
  const themeGold = themed ? Math.round(startingGold * THEME_BONUS) : 0;
  const plain = (Math.round(startingGold * multiplier) + stale + themeGold) * (reminderMultiplier ?? 1);
  // Task roulette's double stacks on top of all of it (owner, 2026-10-05).
  const total = roulette ? plain * GOLD.rouletteMultiplier : plain;
  return {
    base,
    urgent,
    bigJob,
    ageSteps: steps,
    agePeriodMs: agePeriodMs(task),
    ageMultiplier,
    staleGold: stale,
    quickMultiplier: quick,
    flashMultiplier,
    reminderMultiplier,
    streak,
    ...(roulette ? { roulette: true } : {}),
    ...(themeGold > 0 ? { themeGold } : {}),
    total,
    luxury: false,
    ...(projectGrowth !== null ? { project: projectGrowth } : {}),
  };
}

/**
 * The gold an open task's card and task screen show for the viewer: bountyFor's total (with the
 * gold rush `boost` and the viewer's `streakDays` when the caller knows them) and, when `auction` is
 * on (flag auctionUI), a rising luxury task's current total, the offer plus the house's premium
 * (shared/economy.ts auctionPriceNow). The server pays the premium whatever the flag; the flag only
 * decides whether the screens show it. Both clients' cards and task screens read this.
 */
export function openTaskGold(
  task: BountyTask,
  at: number,
  opts: { boost?: number | null; by?: string | null; streakDays?: number; auction?: boolean; reminder?: ReminderRule; themed?: boolean } = {},
): number {
  const bounty = bountyFor(task, at, opts.boost ?? null, opts.by ?? null, opts.streakDays ?? 0, false, opts.reminder, opts.themed === true);
  if (opts.auction && bounty.luxury && task.luxury) {
    const price = auctionPriceNow({ luxury: task.luxury, created_at: task.created_at }, at);
    if (price) return price.total;
  }
  return bounty.total;
}

/**
 * Epoch ms at which `task`'s overdue reminder window closes (GOLD.reminder.windowMs after its
 * overdue push), or null when no push has gone out. The window may already have closed; luxury
 * tasks get no window at all (bountyFor never applies it to them).
 */
export function reminderEndsAt(task: Pick<BountyTask, "alerted_at" | "luxury">): number | null {
  if (!task.alerted_at || (task.luxury && isLuxuryTier(task.luxury))) return null;
  const alerted = Date.parse(task.alerted_at);
  return Number.isFinite(alerted) ? alerted + GOLD.reminder.windowMs : null;
}

/** True while finishing `task` at `at` would pay the overdue reminder doubling: from the push to the window's end, inclusive. */
export function reminderOpen(task: Pick<BountyTask, "alerted_at" | "luxury">, at: number): boolean {
  const ends = reminderEndsAt(task);
  return ends !== null && at >= ends - GOLD.reminder.windowMs && at <= ends;
}

/** Whole minutes left in `task`'s reminder window at `at`, rounded up and at least 1 while open; null when closed. */
export function reminderMinutesLeft(task: Pick<BountyTask, "alerted_at" | "luxury">, at: number): number | null {
  if (!reminderOpen(task, at)) return null;
  return Math.max(1, Math.ceil(((reminderEndsAt(task) as number) - at) / 60_000));
}

/**
 * Which window the overdue reminder's doubling follows (docs/bounties.md "Late chore rush"). With
 * `lateRushUI` off, fifteen minutes from the task's overdue push (reminderOpen), as since
 * 2026-10-01. With it on, `window`: the finisher's (or the viewer's) own late chore rush, or null
 * when they have none open. Build one with reminderRule.
 */
export interface ReminderRule {
  lateRushUI: boolean;
  window: Pick<LateRush, "starts_at" | "ends_at"> | null;
}

/** The rule for a flag state and the viewer's or finisher's late chore rush (a FlashState's late_rush). */
export function reminderRule(lateRushUI: boolean, window: Pick<LateRush, "starts_at" | "ends_at"> | null | undefined): ReminderRule {
  return { lateRushUI, window: window ?? null };
}

/**
 * What the late chore rush reads of a task: whether it went late, whether it is luxury, and whether
 * it is a chore (posted by a schedule). schedule_id is required, so every caller says which it is.
 */
export type LateRushTask = Pick<BountyTask, "alerted_at" | "luxury"> & { schedule_id: string | null };

/**
 * True while `task` is late for the late chore rush: it is a chore (a schedule posted it; owner,
 * 2026-10-14: "I noticed the missed chores gold rush is not only counting chores"), it had its
 * overdue moment (alerted_at) no later than `window`'s end, it is not a luxury task, and `window` is
 * open at `at`. Every such chore pays double, uncapped: the rush is a sweep-up of every late chore.
 * A hand-added task never is, however late.
 */
export function lateRushCovers(task: LateRushTask, window: Pick<LateRush, "starts_at" | "ends_at"> | null | undefined, at: number): boolean {
  if (!task.schedule_id) return false;
  if (!task.alerted_at || (task.luxury && isLuxuryTier(task.luxury))) return false;
  if (!window || !isLateRushActive(window, at)) return false;
  const alerted = Date.parse(task.alerted_at);
  return Number.isFinite(alerted) && alerted <= Date.parse(window.ends_at);
}

/**
 * Whether finishing `task` at `at` pays the overdue reminder's doubling (GOLD.reminder.multiplier).
 * With no rule, or `lateRushUI` off: within fifteen minutes of its overdue push (reminderOpen). With
 * it on: while the rule's late chore rush window covers it (lateRushCovers: chores only), and never
 * otherwise.
 */
export function reminderInForce(task: LateRushTask, at: number, rule?: ReminderRule): boolean {
  return rule?.lateRushUI ? lateRushCovers(task, rule.window, at) : reminderOpen(task, at);
}

/**
 * The task screen's "Double gold for N more min.": whole minutes left on whichever window
 * reminderInForce follows, rounded up and at least 1 while open; null when it pays no doubling.
 */
export function reminderMinutesFor(task: LateRushTask, at: number, rule?: ReminderRule): number | null {
  if (!rule?.lateRushUI) return reminderMinutesLeft(task, at);
  if (!rule.window || !lateRushCovers(task, rule.window, at)) return null;
  return Math.max(1, Math.ceil((Date.parse(rule.window.ends_at) - at) / 60_000));
}

/**
 * Gold at or above which a card shows two coins, and three (owner, 2026-09-30): one coin under
 * 50 gold, two from 50, three from 100.
 */
export const COIN_TIER_THRESHOLDS: { readonly two: number; readonly three: number } = { two: 50, three: 100 };

/**
 * How many coins a task card draws for `total` gold: one for a little, two for some, three for a
 * lot. Cards show coins instead of a number; the exact amount lives on the task screen.
 */
export function goldCoinTier(total: number): 1 | 2 | 3 {
  if (total >= COIN_TIER_THRESHOLDS.three) return 3;
  return total >= COIN_TIER_THRESHOLDS.two ? 2 : 1;
}
